import { useFormContext } from 'react-hook-form';
import { useGetCarQualificationsQuery } from '../../../../../api/carApi';
import Select, { Option } from '../../../../../components/common/Select';
import styles from './styles.module.css';
import { ContractFormData } from '../../../../../types/contracts';
import { ChangeEventHandler, FC } from 'react';
import { max, required } from '../../../../../message';
import FormItem from '../../../../../components/common/FormItem';
import { CarQualification } from '../../../../../types/car';
import CheckBox from '../../../../../components/common/CheckBox';
import TextArea from '../../../../../components/common/TextArea';
import Label from '../../../../../components/common/Label';
import classNames from 'classnames';
import { ErrorMessage } from '@hookform/error-message';
import Caption, {
  CaptionsUnderForm,
} from '../../../../../components/common/Caption';
import { SkillLevel } from '../../../../../types/driver';

const TypedFormItem = FormItem<ContractFormData>;
const setValueOpt = {
  shouldValidate: true,
  shouldDirty: true,
  shouldTouched: true,
};
type FormKeys = keyof Pick<
  ContractFormData,
  'requiredSkillLevelId' | 'requiredQualificationIds' | 'memo'
>;
const labels: Record<FormKeys, string> = {
  requiredSkillLevelId: 'ドライバー制約(習熟度)',
  requiredQualificationIds: 'ドライバー制約必要資格',
  memo: '申し送り',
};
type Variant = 'fit' | 'error';
const SkillAndQualificationForm: FC<{
  skillLevels: SkillLevel[] | undefined;
}> = ({ skillLevels }) => {
  const { getValues, setValue, register, formState, watch } =
    useFormContext<ContractFormData>();

  const skillOptions: Option[] = (skillLevels || []).map((l) => ({
    label: l.skillLevel,
    value: `${l.skillLevelId}`,
  }));
  skillOptions.push({
    label: 'なし',
    value: '0',
  });
  const { name: skillLevelId } = register('requiredSkillLevelId', {
    required: required(labels.requiredSkillLevelId),
  });
  if (getValues(skillLevelId) === null) setValue(skillLevelId, 0, setValueOpt);
  const onSelectSkillLevel: ChangeEventHandler<HTMLSelectElement> = (e) => {
    const value = e.target.value;
    setValue(skillLevelId, Number(value), setValueOpt);
  };
  const skillLevelVariants: Variant[] = formState.errors[skillLevelId]
    ? ['fit', 'error']
    : ['fit'];

  const { data: qualifications } = useGetCarQualificationsQuery();
  const { name: qualificationName } = register('requiredQualificationIds');
  const selectedCarQualifications = watch('requiredQualificationIds');
  const onChangeQualificationIds = (qualification: CarQualification) => {
    const id = qualification.carQualificationId;
    if (selectedCarQualifications.includes(id)) {
      const newValue = selectedCarQualifications.filter((q) => q !== id);
      setValue(qualificationName, newValue, setValueOpt);
    } else {
      const newValue = [...selectedCarQualifications, id];
      setValue(qualificationName, newValue, setValueOpt);
    }
  };
  const hasSomeQualificationsSelected =
    qualifications?.some((q) =>
      selectedCarQualifications.includes(q.carQualificationId)
    ) || false;
  const onCheckAllClear = () => {
    if (hasSomeQualificationsSelected) {
      setValue(qualificationName, [], setValueOpt);
    }
  };

  return (
    <>
      <div className={styles.itemContainer}>
        <TypedFormItem
          label="ドライバー制約(習熟度・必要資格)"
          name={['requiredSkillLevelId', 'requiredQualificationIds']}
        >
          <div className={styles.itemRow}>
            <div className={styles.skill}>
              <Select
                options={skillOptions}
                value={`${watch(skillLevelId)}`}
                onChange={onSelectSkillLevel}
                variants={skillLevelVariants}
              />
            </div>
            <div className={classNames(styles.itemRow, styles.qualifications)}>
              <div className={styles.item}>
                <CheckBox
                  label="なし"
                  value={0}
                  checked={!hasSomeQualificationsSelected}
                  onChange={() => onCheckAllClear()}
                />
              </div>
              {(qualifications || []).map((q) => (
                <div key={q.carQualificationId} className={styles.item}>
                  <CheckBox
                    label={q.carQualification}
                    value={q.carQualificationId}
                    onChange={() => onChangeQualificationIds(q)}
                    checked={selectedCarQualifications.includes(
                      q.carQualificationId
                    )}
                  />
                </div>
              ))}
            </div>
          </div>
        </TypedFormItem>
      </div>
      <div className={styles.itemContainer}>
        <Label optional>{labels.memo}</Label>
        <TextArea
          rows={6}
          {...register('memo', {
            validate: {
              max: (value) => value.length <= 65534 || max(labels.memo, 65534),
            },
          })}
          variants={formState.errors.memo ? ['fit', 'error'] : ['fit']}
        />
        <CaptionsUnderForm>
          <ErrorMessage
            name="memo"
            render={({ message }) => (
              <Caption variants={['error']}>{message}</Caption>
            )}
          />
        </CaptionsUnderForm>
      </div>
    </>
  );
};

export default SkillAndQualificationForm;
